import { useEffect, useMemo, useState } from "react";
import { ImageOff, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { useLines } from "@/lib/components";
import { serializeItem } from "@/lib/pricing";

const urls = new Map();
let productsP = null;
export const loadProducts = (fresh = false) => {
  if (fresh || !productsP) productsP = api.get("/catalog/products").then((r) => r.data).catch((e) => { productsP = null; throw e; });
  return productsP;
};
export const dropPhoto = (key) => urls.delete(key);

export function usePhoto(key, version = 0) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!key) {
      setUrl(null);
      return undefined;
    }
    const k = `${key}:${version}`;
    if (!urls.has(k)) urls.set(k, api.get(`/catalog/photos/${key}`, { responseType: "blob", params: { v: version } }).then((r) => URL.createObjectURL(r.data)).catch(() => { urls.delete(k); return null; }));
    urls.get(k).then((u) => alive && setUrl(u));
    return () => { alive = false; };
  }, [key, version]);
  return url;
}

export function Photo({ k, className = "", testId, version }) {
  const url = usePhoto(k, version);
  return (
    <div data-testid={testId} className={`grid place-items-center overflow-hidden rounded-md bg-white ${className}`}>
      {url ? <img src={url} alt="" className="h-full w-full object-contain" /> : <ImageOff className="h-4 w-4 text-slate-600" />}
    </div>
  );
}

const sel = "h-9 rounded-lg border border-slate-800 bg-slate-900/70 px-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const famOf = (p) => ({ "Sliding windows": "Sliding Window", "Fix Square": "Fixed Square", "Fixed Arquitectural": "Fixed Architectural" })[p.category.replace(/ with Grids?$/i, "")] || p.category.replace(/ with Grids?$/i, "");
const plain = (p) => (p.layout || p.config).length * 100 + p.base_name.length;

function PickerDialog({ onClose, onPick, initial }) {
  const [all, setAll] = useState([]);
  const [f, setF] = useState({ family: "", color: initial.color || "", grid: "", q: "" });
  useEffect(() => { loadProducts().then(setAll).catch((e) => toast.error(errorMessage(e))); }, []);
  const fams = useMemo(() => [...new Set(all.map(famOf))], [all]);
  const shown = all.filter((p) => (!f.family || famOf(p) === f.family) && (!f.color || p.color === f.color)
    && (f.grid === "" || String(p.grid) === f.grid) && (!f.q || `${p.name} ${p.code}`.toLowerCase().includes(f.q.toLowerCase())))
    .sort((a, b) => plain(a) - plain(b)).slice(0, 120);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-5xl overflow-y-auto" data-testid="catalog-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Choose a Viewlux product</DialogTitle>
          <DialogDescription>Filter by category, color and grid. The price comes from the category's supplier line.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <select data-testid="catalog-filter-category" value={f.family} onChange={set("family")} className={sel}>
            <option value="">All categories</option>{fams.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="catalog-filter-color" value={f.color} onChange={set("color")} className={sel}>
            <option value="">All colors</option>{["Black", "Bronze", "Gray", "White"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <select data-testid="catalog-filter-grid" value={f.grid} onChange={set("grid")} className={sel}>
            <option value="">Grid or not</option><option value="true">With grid</option><option value="false">No grid</option>
          </select>
          <label className="relative min-w-[200px] flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
            <input data-testid="catalog-filter-search" value={f.q} onChange={set("q")} placeholder="Search name or code" className={`${sel} w-full pl-8`} />
          </label>
        </div>
        <p data-testid="catalog-picker-count" className="text-xs text-slate-500">{shown.length}{shown.length === 120 ? "+" : ""} products</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {shown.map((p, i) => (
            <button key={p.id} data-testid={`catalog-product-${i}`} onClick={() => onPick(p)}
              className="rounded-xl border border-slate-800 p-2 text-left transition-colors hover:border-amber-500/70">
              <Photo k={p.photo_key} className="h-28 w-full" />
              <p className="mt-2 line-clamp-2 text-xs font-medium text-slate-100">{p.name}</p>
              <p className="font-mono-num text-[10px] text-slate-500">{p.code} · {p.layout || p.config}</p>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ProductPicker({ row, idx, onPatch }) {
  const { rates } = useLines();
  const [open, setOpen] = useState(false);
  const pick = async (p) => {
    try {
      const { data } = await api.post(`/catalog/products/${p.id}/apply`, serializeItem(row));
      const pid = data.product_line_id;
      onPatch({ ...data, component: pid && rates[pid] ? { line: rates[pid] } : {}, component_unit_sell: null, shape_id: null, shape_name: "", shape_style: "", screen_id: null, screen_name: "" });
      setOpen(false);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <>
      <button data-testid={`quote-item-catalog-button-${idx}`} onClick={() => setOpen(true)} title={row.product_name || "Choose a catalog product"}
        className="mt-1 flex w-full items-center gap-2 rounded-md border border-dashed border-slate-800 px-1.5 py-1 text-left text-[11px] text-slate-400 transition-colors hover:border-amber-500/60">
        {row.catalog_id ? <Photo k={row.catalog_photo} className="h-7 w-7 shrink-0" /> : null}
        <span data-testid={`quote-item-catalog-name-${idx}`} className="truncate">{row.catalog_id ? row.product_name : "Viewlux product…"}</span>
      </button>
      {open && <PickerDialog onClose={() => setOpen(false)} onPick={pick} initial={{ color: row.ext_color }} />}
    </>
  );
}
